/**
 * PagesPanel
 *
 * Post-generation page management: add, remove, reorder, rename, show/hide.
 * Sits in the "Pages" sidebar tab.
 *
 * The backend for all of this shipped in Feb 2026 (`page-management.ts`) and
 * had no UI until now — every operation here was curl-only.
 *
 * Two things about the data model worth knowing before editing this file:
 *
 *  1. The home page is keyed 'home' in the content model but stored as 'index'
 *     in rawPages. Everything the panel touches uses the 'home' form; the
 *     backend translates.
 *  2. `pageList` (what pages exist) and `navigation.pages` (what's in the menu,
 *     and in what order) are separate collections that can legitimately
 *     disagree — a page can exist without a nav entry. The list below joins
 *     them, ordering by nav position and appending anything nav doesn't know
 *     about, so an orphaned page is visible rather than silently missing.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  FileText, Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff,
  Loader2, AlertTriangle, Pencil, Check, X, RefreshCw,
} from 'lucide-react';
import {
  addPage, removePage, reorderNavigation, updateNavItem, rebuildScaffold,
} from '../../api/editor';
import type { PageRole } from '../../api/editor';
import type { EditorPageInfo, EditableSiteContent } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface PagesPanelProps {
  websiteId: string;
  /** Pages that exist, from the content model. */
  pageList: EditorPageInfo[];
  /** Navigation order/labels/visibility. */
  navigation: EditableSiteContent['navigation'] | null;
  /** Currently previewed page, so we can indicate it. */
  activePage: string;
  /** Switch the preview to a page. */
  onSelectPage: (slug: string) => void;
  /** Reload the content model + refresh the preview. */
  onPagesChanged: () => void;
}

/** A page joined with its navigation entry. */
interface PageRow {
  slug: string;
  title: string;
  sectionCount: number;
  navLabel: string;
  visible: boolean;
  /** Position in the nav order, or -1 when nav has no entry for it. */
  navIndex: number;
}

/** Backend cap in `addPageWithContent`. */
const MAX_PAGES = 10;

/** Section hints offered on the add form. Matches the V4 section renderers. */
const SECTION_HINTS = [
  'hero', 'services', 'about', 'testimonials', 'gallery',
  'stats', 'process', 'team', 'faq', 'cta', 'contact',
];

// =============================================================================
// HELPERS
// =============================================================================

/** Title → slug, matching the backend's sanitizer so the preview is honest. */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Join pages with their nav entries.
 * Nav order wins; pages nav doesn't know about are appended.
 */
function buildRows(
  pageList: EditorPageInfo[],
  navigation: EditableSiteContent['navigation'] | null
): PageRow[] {
  const navPages = navigation?.pages ?? [];
  const navBySlug = new Map(navPages.map((p, i) => [p.slug, { ...p, index: i }]));

  return pageList
    .map((page) => {
      const nav = navBySlug.get(page.slug);
      return {
        slug: page.slug,
        title: page.title,
        sectionCount: page.sectionCount,
        navLabel: nav?.label ?? page.title,
        visible: nav?.visible ?? false,
        navIndex: nav?.index ?? -1,
      };
    })
    .sort((a, b) => {
      // Pages missing from nav sort last, keeping their relative order.
      if (a.navIndex === -1 && b.navIndex === -1) return 0;
      if (a.navIndex === -1) return 1;
      if (b.navIndex === -1) return -1;
      return a.navIndex - b.navIndex;
    });
}

// =============================================================================
// COMPONENT
// =============================================================================

export function PagesPanel({
  websiteId,
  pageList,
  navigation,
  activePage,
  onSelectPage,
  onPagesChanged,
}: PagesPanelProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [newSections, setNewSections] = useState<string[]>([]);
  const [newRole, setNewRole] = useState<PageRole | ''>('');

  // Inline rename
  const [renamingSlug, setRenamingSlug] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Two-step delete
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const rows = useMemo(() => buildRows(pageList, navigation), [pageList, navigation]);
  const visibleCount = rows.filter((r) => r.visible).length;
  const atPageCap = rows.length >= MAX_PAGES;

  // Reordering operates on nav positions, not list positions. Pages missing
  // from nav render at the bottom of the list but take no part in ordering, so
  // using the row index for the arrow disabled-state would enable a "down" on
  // the last nav page whenever an orphan sat below it — a button that looks
  // live and does nothing.
  const navOrderedSlugs = useMemo(
    () => rows.filter((r) => r.navIndex !== -1).map((r) => r.slug),
    [rows]
  );

  /** Run a mutation, then reload. Every op here rewrites all pages. */
  const run = useCallback(
    async (
      key: string,
      op: () => Promise<{ success: boolean; error?: string }>,
      successMessage?: string
    ): Promise<boolean> => {
      setBusy(key);
      setError(null);
      setNotice(null);
      try {
        const result = await op();
        if (!result.success) {
          setError(result.error || 'Operation failed');
          return false;
        }
        if (successMessage) setNotice(successMessage);
        onPagesChanged();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Request failed');
        return false;
      } finally {
        setBusy(null);
      }
    },
    [onPagesChanged]
  );

  const handleAdd = useCallback(async () => {
    const slug = slugTouched ? slugify(newSlug) : slugify(newTitle);
    if (!slug || !newTitle.trim() || !newDescription.trim()) {
      setError('Title, slug, and description are all required');
      return;
    }
    if (rows.some((r) => r.slug === slug)) {
      setError(`A page with the slug "${slug}" already exists`);
      return;
    }

    setBusy('add');
    setError(null);
    setNotice(null);
    try {
      const result = await addPage(websiteId, {
        slug,
        title: newTitle.trim(),
        description: newDescription.trim(),
        sections: newSections.length > 0 ? newSections : undefined,
        role: newRole || undefined,
      });

      if (!result.success) {
        setError(result.error || 'Page generation failed');
        return;
      }

      const cost = result.cost != null ? ` (${result.cost.toFixed(3)} USD)` : '';
      setNotice(`Created "${result.generatedSlug || slug}"${cost}`);
      setShowAddForm(false);
      setNewTitle('');
      setNewSlug('');
      setSlugTouched(false);
      setNewDescription('');
      setNewSections([]);
      setNewRole('');
      onPagesChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  }, [websiteId, newTitle, newSlug, slugTouched, newDescription, newSections, newRole, rows, onPagesChanged]);

  const handleMove = useCallback(
    (slug: string, direction: -1 | 1) => {
      // Reorder only the pages nav actually knows about — sending a slug nav
      // has no entry for would be inventing one.
      const from = navOrderedSlugs.indexOf(slug);
      const to = from + direction;
      if (from === -1 || to < 0 || to >= navOrderedSlugs.length) return;

      const next = [...navOrderedSlugs];
      [next[from], next[to]] = [next[to], next[from]];
      void run(`move:${slug}`, () => reorderNavigation(websiteId, next));
    },
    [navOrderedSlugs, websiteId, run]
  );

  const handleToggleVisible = useCallback(
    (row: PageRow) => {
      // The backend refuses to drop below 2 visible nav pages when REMOVING a
      // page. Hiding is a different endpoint with no such guard, so a site can
      // be left with a one-item menu. Warn rather than block — there are real
      // designs where the logo is the only route home.
      if (row.visible && visibleCount <= 2) {
        setNotice('Heads up: this leaves fewer than 2 items in the menu.');
      }
      void run(`visible:${row.slug}`, () =>
        updateNavItem(websiteId, row.slug, { visible: !row.visible })
      );
    },
    [websiteId, run, visibleCount]
  );

  const handleRename = useCallback(
    async (slug: string) => {
      const label = renameValue.trim();
      if (!label) {
        setError('Nav label cannot be empty');
        return;
      }
      const ok = await run(`rename:${slug}`, () => updateNavItem(websiteId, slug, { label }));
      if (ok) setRenamingSlug(null);
    },
    [websiteId, renameValue, run]
  );

  const handleDelete = useCallback(
    async (slug: string) => {
      const ok = await run(
        `delete:${slug}`,
        () => removePage(websiteId, slug),
        `Deleted "${slug}"`
      );
      if (ok) {
        setConfirmingDelete(null);
        if (activePage === slug) onSelectPage('home');
      }
    },
    [websiteId, run, activePage, onSelectPage]
  );

  const handleRebuild = useCallback(() => {
    void run('rebuild', () => rebuildScaffold(websiteId), 'Scaffold rebuilt on all pages');
  }, [websiteId, run]);

  const isBusy = busy !== null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">Pages</span>
          <span className="text-xs text-gray-400">
            {rows.length}/{MAX_PAGES}
          </span>
        </div>
        <button
          onClick={handleRebuild}
          disabled={isBusy}
          title="Rebuild header, nav and footer on every page from current client info"
          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-40"
        >
          {busy === 'rebuild' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mx-3 mt-2 px-2.5 py-2 bg-red-50 border border-red-200 rounded flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}
      {notice && !error && (
        <div className="mx-3 mt-2 px-2.5 py-2 bg-green-50 border border-green-200 rounded">
          <span className="text-xs text-green-700">{notice}</span>
        </div>
      )}

      {/* Page list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {rows.map((row) => {
          const isHome = row.slug === 'home';
          const inNav = row.navIndex !== -1;
          const isActive = row.slug === activePage;
          const rowBusy = busy?.endsWith(`:${row.slug}`);
          const navPos = navOrderedSlugs.indexOf(row.slug);

          return (
            <div
              key={row.slug}
              className={`border rounded-lg px-2.5 py-2 transition-colors ${
                isActive ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2">
                {/* Reorder */}
                <div className="flex flex-col -my-1">
                  <button
                    onClick={() => handleMove(row.slug, -1)}
                    disabled={isBusy || !inNav || navPos <= 0}
                    className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    title="Move up"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleMove(row.slug, 1)}
                    disabled={isBusy || !inNav || navPos === navOrderedSlugs.length - 1}
                    className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    title="Move down"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Title + slug */}
                <button
                  onClick={() => onSelectPage(row.slug)}
                  className="flex-1 text-left min-w-0"
                >
                  {renamingSlug === row.slug ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleRename(row.slug);
                        if (e.key === 'Escape') setRenamingSlug(null);
                      }}
                      className="w-full px-1.5 py-0.5 text-sm border border-blue-400 rounded focus:outline-none"
                    />
                  ) : (
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {row.navLabel}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 truncate">
                    /{row.slug === 'home' ? '' : row.slug}
                    {' · '}
                    {row.sectionCount} section{row.sectionCount === 1 ? '' : 's'}
                    {!inNav && ' · not in menu'}
                  </div>
                </button>

                {/* Actions */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {rowBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />}

                  {renamingSlug === row.slug ? (
                    <>
                      <button
                        onClick={() => void handleRename(row.slug)}
                        disabled={isBusy}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Save label"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setRenamingSlug(null)}
                        className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setRenamingSlug(row.slug);
                          setRenameValue(row.navLabel);
                        }}
                        disabled={isBusy || !inNav}
                        className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-30"
                        title="Rename menu label"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleToggleVisible(row)}
                        disabled={isBusy || !inNav}
                        className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-30"
                        title={row.visible ? 'Hide from menu' : 'Show in menu'}
                      >
                        {row.visible ? (
                          <Eye className="w-3.5 h-3.5" />
                        ) : (
                          <EyeOff className="w-3.5 h-3.5 text-gray-300" />
                        )}
                      </button>
                      {!isHome && (
                        <button
                          onClick={() => setConfirmingDelete(row.slug)}
                          disabled={isBusy}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                          title="Delete page"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Delete confirmation */}
              {confirmingDelete === row.slug && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600 mb-2">
                    Delete <strong>{row.navLabel}</strong> and its content? This can't be undone
                    from here — you'd need to restore a previous version.
                  </p>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => void handleDelete(row.slug)}
                      disabled={isBusy}
                      className="px-2.5 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add page */}
      <div className="border-t border-gray-200 p-3">
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            disabled={isBusy || atPageCap}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-40 disabled:hover:bg-transparent"
            title={atPageCap ? `Sites are capped at ${MAX_PAGES} pages` : undefined}
          >
            <Plus className="w-4 h-4" />
            {atPageCap ? `Page limit reached (${MAX_PAGES})` : 'Add page'}
          </button>
        ) : (
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Financing"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                URL
                <span className="ml-1 font-normal text-gray-400">
                  /{(slugTouched ? slugify(newSlug) : slugify(newTitle)) || '…'}
                </span>
              </label>
              <input
                value={slugTouched ? newSlug : slugify(newTitle)}
                onChange={(e) => {
                  setSlugTouched(true);
                  setNewSlug(e.target.value);
                }}
                placeholder="financing"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                What's this page for?
              </label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={3}
                placeholder="Explain financing options for HVAC replacement — monthly payment plans, who qualifies, how to apply."
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded resize-none focus:outline-none focus:border-blue-400"
              />
              <p className="mt-1 text-xs text-gray-400">
                This is the AI's brief. Specific beats short.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Page type
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as PageRole | '')}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400"
              >
                <option value="">Automatic (from the title)</option>
                <option value="portfolio">Portfolio / gallery</option>
                <option value="offers">Offers / specials / packages</option>
                <option value="booking">Booking / appointments</option>
                <option value="menu">Menu</option>
                <option value="pricing">Pricing</option>
                <option value="shop">Shop</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">
                Decides which page the nav button promotes in place of Contact.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sections <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-1">
                {SECTION_HINTS.map((section) => {
                  const selected = newSections.includes(section);
                  return (
                    <button
                      key={section}
                      onClick={() =>
                        setNewSections((prev) =>
                          selected ? prev.filter((s) => s !== section) : [...prev, section]
                        )
                      }
                      className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                        selected
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {section}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Generates content with AI — costs a few cents and takes up to a minute.
              Nav updates on every page.
            </p>

            <div className="flex gap-1.5">
              <button
                onClick={() => void handleAdd()}
                disabled={isBusy}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
              >
                {busy === 'add' ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Generating…
                  </>
                ) : (
                  'Create page'
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setError(null);
                }}
                disabled={isBusy}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
